/**
 * SPEC-TrainingSimulator §8.3 / §8.3a — the Phase 1–2 question rail panel.
 *
 * Column layout (2026-09-09, was a `200px | 1fr` grid): a horizontal Q1–Q10
 * chip strip ABOVE the active question card (done = green tick / red cross,
 * active = purple, pending = grey, not asked = dimmed dash). Numbered chips
 * put the whole run in one glance and land the ✓/✗ directly under Check
 * answer; the active question's name lives in the strip's caption, which the
 * old vertical list carried on every row. The card is the SAME
 * `QuestionForm` the key editor uses (question-controls.tsx), so the answer
 * shape is the key shape (D12) and q1/q2/q7 are captured only by highlighting
 * (no typed input, 2026-09-09). Nothing here reads the key: the graded result
 * (grade, resolved feedback text, keyed answer, pointers) arrives from the
 * server after **Check answer** and is rendered as the feedback card.
 *
 * Back (D20): inside a question un-picks the last change (the page keeps a
 * history stack per question); across questions a graded one can be
 * revisited read-only. Nothing graded can be changed.
 *
 * Phase 2 is the SAME strip in `silent` mode (Sarah's call, 2026-09-09):
 * identical chips and card, but "Submit answer" records and advances with no
 * ✓/✗, no feedback card and no reveal — a submitted chip goes a neutral
 * purple "answered" and stays clickable, because §8.3a lets a Phase 2 trainee
 * reopen and CHANGE any earlier question until the q10 decision. Everything the
 * key knows arrives at once on the review screen (D18).
 */

import type { ReactNode } from "react";
import {
  Q10_ROWS,
  Q7_UNCLEAR_LABELS,
  Q2_IN_POD_NA_LABEL,
  normalizeReferenceEntry,
  entryComplete,
  isNA,
  notAskedReason,
  type AnswerEntry,
  type AnswerKey,
  type FeedbackResolution,
  type QuestionGrade,
  type QuestionId,
  type QuestionReveal,
  type SimulatorQuestionConfig,
} from "@shared/simulator-helpers";
import { DZE_OPTION_LABELS } from "@/lib/prescreen-helpers";
import { Starling } from "@/lib/starling-brand";
import { QUESTION_SHORT_LABEL, QuestionForm, type EvidenceOption } from "@/components/simulator/question-controls";
import { isRevisit } from "@/lib/simulator-runner-helpers";

const T = {
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  sub: "#F3F4F6",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.08)",
  purpleSubB: "rgba(89,47,150,.25)",
  good: "#166534",
  goodBg: "#DCFCE7",
  goodBorder: "#86EFAC",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  badBorder: "#FECACA",
} as const;

export interface RailGraded {
  answer: AnswerEntry;
  grade: QuestionGrade;
  feedback: FeedbackResolution;
  reveal: QuestionReveal;
}

export interface QuestionRailProps {
  /** Every rail question for the phase, in order (Q8 included). */
  questions: SimulatorQuestionConfig[];
  /**
   * Questions the KEY does not ask (the server's `asked` verdict, known once
   * Q7 is graded). They stay in the rail, dimmed as "not asked", so the
   * count never shifts; they are never active (audit W1 / W7).
   */
  skipped: QuestionId[];
  activeId: QuestionId;
  onActiveChange: (id: QuestionId) => void;
  drafts: Partial<Record<QuestionId, AnswerEntry | null>>;
  onDraftChange: (id: QuestionId, next: AnswerEntry | null) => void;
  graded: Partial<Record<QuestionId, RailGraded>>;
  /** Un-pick the last change on the active question (D20). */
  onBack: () => void;
  canBack: boolean;
  onCheck: () => void;
  checking: boolean;
  /** After a graded question: advance (next question, or the next delivery after q10). */
  onNext: () => void;
  advancing: boolean;
  evidence: EvidenceOption[];
  completionNoteElementId: string;
  deliveryDetailsElementId: string;
  phase: number;
  /**
   * Height of the clone's hero card, so the strip fills the same row (the
   * runner measures it; undefined outside that layout).
   */
  stripMinHeight?: number;
  /** Label for the button after q10 is graded ("Next delivery" / "Finish phase"). */
  finalLabel: string;
  /** Phase 2: record and advance, no instant feedback (§8.3). */
  silent?: boolean;
  /** Phase 2: answers already sent for this item — the neutral "answered" chips. */
  submitted?: Partial<Record<QuestionId, AnswerEntry>>;
}

const FIELD_LABELS: Record<string, string> = {
  value: "Value", inPod: "Visible in POD", where: "Where", otherSeen: "Different value seen", otherValue: "Which value", unclearReason: "Why unclear",
  clearWhy: "Clear why the driver deviated", conflict: "Past photos show a conflict", differs: "Which looks wrong", quote: "Quote", decision: "Decision",
  "rows.street": "Street row", "rows.unit": "Unit row", "rows.dz": "Drop zone row", "rows.returns": "Returns row",
};

const yn = (v: unknown, options: Record<string, string>) => (typeof v === "string" ? options[v] ?? v : "—");

/** One-line, human description of an entry (the trainee's answer or the keyed answer). */
export function describeEntry(id: QuestionId, entry: AnswerEntry | null | undefined, config: SimulatorQuestionConfig, evidence: EvidenceOption[]): string {
  if (!entry) return "—";
  const e = entry as any;
  const options = config.text.options ?? {};
  const labels = config.text.labels ?? {};
  const where = (w: unknown) => (Array.isArray(w) && w.length ? w.map((s) => evidence.find((o) => o.value === s)?.label ?? s).join(", ") : "—");
  switch (id) {
    case "q1_street":
    case "q2_unit": {
      // N/A is terminal for q2: no POD sub-questions to describe.
      if (id === "q2_unit" && isNA(e.value)) return labels.na ?? "N/A";
      const parts = [`${e.value ?? "—"}`, `${labels.inPod ?? "In POD?"} ${e.inPod === "na" ? (options.inPodNa ?? Q2_IN_POD_NA_LABEL) : yn(e.inPod, options)}`];
      if (e.inPod === "yes" || e.inPod === "unclear") parts.push(`where: ${where(e.where)}`);
      if (e.inPod === "no") { parts.push(`different value seen: ${yn(e.otherSeen, options)}`); if (e.otherSeen === "yes") parts.push(`${e.otherValue ?? "—"} in ${where(e.where)}`); }
      if (e.inPod === "unclear") parts.push(yn(e.unclearReason, options));
      return parts.join(" · ");
    }
    case "q3_expected_dz":
      return e.value ? `${e.value} · ${DZE_OPTION_LABELS[e.value as keyof typeof DZE_OPTION_LABELS] ?? ""}` : "—";
    case "q4_actual_dz":
      return `${e.value ?? "—"}${e.value ? ` · ${DZE_OPTION_LABELS[e.value as keyof typeof DZE_OPTION_LABELS] ?? ""}` : ""} · where: ${where(e.where)}`;
    case "q5_meets":
      return `${yn(e.value, options)}${e.value === "no" ? ` · clear why: ${yn(e.clearWhy, options)}` : ""}`;
    case "q6_reference": {
      const r = normalizeReferenceEntry(e) as any;
      return `${yn(r.value, options)}${r.value === "yes" ? ` · conflict: ${yn(r.conflict, options)}` : ""}${r.conflict === "yes" && Array.isArray(r.differs) ? ` · wrong: ${r.differs.map((d: string) => options[d] ?? d).join(", ")}` : ""}`;
    }
    case "q7_return_note":
      return `${yn(e.value, options)}${e.value === "unclear" ? ` · ${yn(e.unclearReason, { ...Q7_UNCLEAR_LABELS, ...options })}` : e.quote ? ` · “${e.quote}”` : ""}`;
    case "q8_missed_return":
      return `${yn(e.value, options)}${e.value === "yes" ? ` · where: ${where(e.where)}` : ""}`;
    case "q9_condition":
      return yn(e.value, options);
    case "q10_decision": {
      const rows = e.rows ?? {};
      const rowLabel: Record<string, string> = { street: labels.street ?? "Street", unit: labels.unit ?? "Unit", dz: labels.dz ?? "Drop zone", returns: labels.returns ?? "Returns" };
      // Rows the delivery did not ask (unit on an N/A-unit delivery) are absent, not "—".
      return `${Q10_ROWS.filter((r) => rows[r] !== undefined).map((r) => `${rowLabel[r]}: ${yn(rows[r], { na: "N/A", ...options })}`).join(" · ")} → ${e.decision === "GOOD" ? "Good" : e.decision === "BAD" ? "Bad" : "—"}`;
    }
    default:
      return e.value ?? "—";
  }
}

function Btn({ children, onClick, disabled, kind, testId, title }: { children: ReactNode; onClick: () => void; disabled?: boolean; kind: "primary" | "ghost"; testId: string; title?: string }) {
  const primary = kind === "primary";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: primary ? "8px 18px" : "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: Starling.fontBody, cursor: disabled ? "not-allowed" : "pointer",
        border: `1px solid ${primary ? T.purple : T.border}`, background: primary ? T.purple : T.card, color: primary ? "#fff" : T.txt2, opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function QuestionRail(props: QuestionRailProps) {
  const { questions, skipped, activeId, onActiveChange, drafts, onDraftChange, graded, onBack, canBack, onCheck, checking, onNext, advancing, evidence, completionNoteElementId, deliveryDetailsElementId, phase, stripMinHeight, finalLabel, silent, submitted } = props;
  const sent = submitted ?? {};
  const skip = new Set(skipped);
  const active = questions.find((q) => q.id === activeId) ?? questions[0];
  if (!active) return null;
  const activeIndex = questions.findIndex((q) => q.id === active.id);
  const result = graded[active.id];
  const draft = drafts[active.id] ?? null;
  // Completeness is judged with the graded answers as context; whether the
  // question is asked at all comes from `skipped` (the key's verdict), so the
  // trainee's own Q7 never hides Q8 (W1).
  const context: AnswerKey = {};
  for (const [qid, g] of Object.entries(graded)) if (g) (context as any)[qid] = g.answer;
  for (const [qid, a] of Object.entries(sent)) if (a) (context as any)[qid] = a;
  // Whether Q10 asks "Unit correct?" follows the KEY's q2 once Phase 1 has
  // revealed it (a trainee who wrongly picked N/A is still asked the row);
  // before that, or in silent Phase 2, the trainee's own q2 decides.
  const keyedUnit = graded.q2_unit?.reveal?.keyed;
  if (keyedUnit) (context as any).q2_unit = keyedUnit;
  /** "This question has been dealt with" — graded in Phase 1, sent in Phase 2. */
  const answeredIds = new Set<QuestionId>([
    ...(Object.keys(graded) as QuestionId[]).filter((k) => graded[k]),
    ...(Object.keys(sent) as QuestionId[]).filter((k) => sent[k]),
  ]);
  const complete = entryComplete(active.id, draft, { key: context, phase });
  const firstPending = questions.find((q) => !answeredIds.has(q.id) && !skip.has(q.id));
  const latestGraded = [...questions].reverse().find((q) => graded[q.id]);
  const viewingOld = isRevisit(questions, active.id, graded);
  const isLast = !questions.slice(activeIndex + 1).some((q) => !skip.has(q.id));
  // Counts are over EVERY rail question, so the caption's N matches the card's
  // and never shifts when the key drops Q8 (W7, AUDIT-TraineeUI L1).
  const gradedCount = questions.filter((q) => answeredIds.has(q.id)).length;
  const notAskedCount = questions.filter((q) => skip.has(q.id)).length;

  return (
    <div data-testid="question-rail-panel" style={{ display: "flex", flexDirection: "column", gap: 13, fontFamily: Starling.fontBody, color: T.text }}>
      {/* Progress strip — sits in the clone's hero row (§8.3). */}
      <div
        data-testid="rail-progress"
        style={{
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 1px 2px rgba(16,24,40,.06)",
          padding: "10px 12px", minHeight: stripMinHeight, display: "flex", flexDirection: "column", justifyContent: "center", gap: 9,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <span data-testid="rail-active-label" style={{ fontSize: 12.5, fontWeight: 700, color: T.purple, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Q{active.sort} · {QUESTION_SHORT_LABEL[active.id]}
          </span>
          <span data-testid="rail-progress-count" style={{ fontSize: 11, color: T.txt3, whiteSpace: "nowrap" }}>
            {gradedCount} of {questions.length} answered{notAskedCount > 0 ? ` · ${notAskedCount} not asked` : ""}
          </span>
        </div>
        <div data-testid="question-rail" style={{ display: "flex", gap: 4 }}>
          {questions.map((q, i) => {
            const done = answeredIds.has(q.id);
            const isActive = q.id === active.id;
            const notAsked = skip.has(q.id);
            const clickable = !notAsked && (done || isActive);
            // Silent (Phase 2): a done chip says "answered" and nothing more —
            // ✓/✗ would be the feedback the phase is meant to withhold.
            const correctChip = !silent && done && graded[q.id]?.grade.status === "correct";
            const status = notAsked ? "skipped" : isActive ? "active" : done ? (silent ? "answered" : "done") : "pending";
            return (
              <button
                key={q.id}
                type="button"
                data-testid={`rail-step-${q.id}`}
                data-status={status}
                aria-current={isActive ? "step" : undefined}
                aria-label={`Q${q.sort} · ${QUESTION_SHORT_LABEL[q.id]}${notAsked ? " · not asked" : !done ? "" : silent ? " · answered" : graded[q.id]?.grade.status === "correct" ? " · correct" : " · not quite"}`}
                disabled={!clickable}
                onClick={() => clickable && onActiveChange(q.id)}
                title={notAsked ? `Not asked for this delivery — ${notAskedReason(q.id)}` : `Q${q.sort} · ${QUESTION_SHORT_LABEL[q.id]}`}
                style={{
                  flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "10px 0", borderRadius: 8, fontSize: 13.5, fontFamily: Starling.fontBody,
                  cursor: clickable ? "pointer" : "default",
                  border: `1px ${notAsked ? "dashed" : "solid"} ${isActive ? T.purple : !done ? T.border : silent ? T.purpleSubB : correctChip ? T.goodBorder : T.badBorder}`,
                  background: isActive ? T.purpleSub : !done ? (notAsked ? T.sub : T.card) : silent ? T.purpleSub : correctChip ? T.goodBg : T.badBg,
                  color: isActive ? T.purple : !done ? (notAsked ? T.txt2 : T.txt3) : silent ? T.purple : correctChip ? T.good : T.bad,
                  fontWeight: isActive ? 700 : 600,
                  boxShadow: isActive ? `0 0 0 2px ${T.purpleSubB}` : "none",
                }}
              >
                {!done ? (notAsked ? "–" : i + 1) : silent ? i + 1 : correctChip ? "✓" : "✗"}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active card */}
      <div
        key={active.id}
        data-testid={`rail-question-${active.id}`}
        data-graded={result ? "true" : "false"}
        style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", borderLeft: `4px solid ${result ? (result.grade.status === "correct" ? T.good : T.bad) : T.purple}`, minWidth: 0 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
          <div style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 15 }}>
            <span style={{ color: T.purple, marginRight: 8 }}>Q{active.sort}</span>{active.text.prompt}
          </div>
          <div style={{ fontSize: 11, color: T.txt3, whiteSpace: "nowrap" }}>{activeIndex + 1} of {questions.length}</div>
        </div>

        {!result ? (
          <>
            <QuestionForm
              config={active}
              entry={draft}
              onChange={(next) => onDraftChange(active.id, next)}
              evidence={evidence}
              answerKey={{ ...context, [active.id]: draft }}
              completionNoteElementId={completionNoteElementId}
              deliveryDetailsElementId={deliveryDetailsElementId}
              applies={!skip.has(active.id)}
            />
            {silent && sent[active.id] && (
              <div data-testid="rail-already-sent" style={{ fontSize: 12, color: T.txt2, marginTop: 10 }}>
                Already answered — you can change it until you decide Good or Bad.
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 10 }}>
              <Btn kind="ghost" testId="rail-back" onClick={onBack} disabled={!canBack} title="Un-pick the last choice">‹ Back</Btn>
              <Btn
                kind="primary"
                testId="rail-check"
                onClick={onCheck}
                disabled={!complete || checking || advancing}
                title={complete ? (silent ? "Record this answer and move on" : "Grade this answer") : "Finish the answer first"}
              >
                {checking || advancing ? "Saving…" : silent ? (isLast ? finalLabel : "Submit answer") : "Check answer"}
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: T.txt2, marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: T.text }}>You answered:</span> {describeEntry(active.id, result.answer, active, evidence)}
            </div>
            <FeedbackCard id={active.id} config={active} result={result} evidence={evidence} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 10 }}>
              {viewingOld ? (
                <>
                  <span style={{ fontSize: 12, color: T.txt3 }}>Graded — read only.</span>
                  <Btn kind="primary" testId="rail-next" onClick={() => onActiveChange((firstPending ?? latestGraded ?? active).id)}>Back to Q{(firstPending ?? latestGraded ?? active).sort} ›</Btn>
                </>
              ) : (
                <>
                  <Btn kind="ghost" testId="rail-back" onClick={() => activeIndex > 0 && onActiveChange(questions[activeIndex - 1].id)} disabled={activeIndex === 0} title="Re-read the previous question">‹ Back</Btn>
                  <Btn kind="primary" testId="rail-next" onClick={onNext} disabled={advancing}>
                    {advancing ? "…" : isLast ? finalLabel : "Next question ›"}
                  </Btn>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** §8.3 — the correct / wrong card with the resolved feedback and the keyed answer. */
export function FeedbackCard({ id, config, result, evidence }: { id: QuestionId; config: SimulatorQuestionConfig; result: RailGraded; evidence: EvidenceOption[] }) {
  const correct = result.grade.status === "correct";
  const wrongFields = Object.entries(result.grade.fields).filter(([, s]) => s === "wrong" || s === "no_answer").map(([f]) => FIELD_LABELS[f] ?? f);
  return (
    <div
      data-testid="rail-feedback"
      data-status={result.grade.status}
      style={{
        borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6,
        background: correct ? T.goodBg : T.badBg, border: `1px solid ${correct ? T.goodBorder : T.badBorder}`, color: T.text,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, color: correct ? T.good : T.bad }}>{correct ? "✓ Correct" : "✗ Not quite"}</div>
      <div data-testid="rail-feedback-text" style={{ fontSize: 13, lineHeight: 1.45 }}>{result.feedback.text}</div>
      {!correct && wrongFields.length > 0 && (
        <div style={{ fontSize: 12, color: T.txt2 }}>Wrong on: {wrongFields.join(", ")}</div>
      )}
      <div data-testid="rail-feedback-keyed" style={{ fontSize: 12.5, borderTop: `1px dashed ${correct ? T.goodBorder : T.badBorder}`, paddingTop: 6 }}>
        <span style={{ fontWeight: 600 }}>Keyed answer:</span> {describeEntry(id, result.reveal.keyed, config, evidence)}
      </div>
      {(result.reveal.boxes.length > 0 || result.reveal.highlights.length > 0 || result.reveal.valueHighlight) && (
        <div style={{ fontSize: 11.5, color: T.txt2 }}>Look for the green outlines on the delivery — they show where the answer is.</div>
      )}
    </div>
  );
}
